import React, { useEffect, useMemo, useState } from 'react';
import DataGrid from 'react-data-grid';
import { Cross2Icon } from '../icons/Icons';
import { getMiniDCSessionList, LoadSession, IsPlateCorSetup } from '../isdlData/sessionData';
import './Components.css';
import './custom/rdgstyles.css';

function ImportPlateShotsModal({ currentSession, currentSetup, onComplete, theme = 'dark' }) {
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [selectedSourceSetupId, setSelectedSourceSetupId] = useState(null);
    const [selectedSessionPreview, setSelectedSessionPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [replaceExisting, setReplaceExisting] = useState(true);
    const [filterText, setFilterText] = useState('');

    useEffect(() => {
        const loadSessions = async () => {
            try {
                const dataCollectorId = currentSession?.DataCollectorId;
                if (!dataCollectorId) {
                    setSessions([]);
                    setLoading(false);
                    return;
                }

                const miniSessions = await getMiniDCSessionList(dataCollectorId);
                const normalized = Array.isArray(miniSessions) ? miniSessions : (miniSessions ? [miniSessions] : []);
                const nextSessions = normalized.filter((session) => session?.id).reverse();
                setSessions(nextSessions);
            } finally {
                setLoading(false);
            }
        };

        loadSessions();
    }, [currentSession?.DataCollectorId, currentSession?.id]);

    useEffect(() => {
        const loadPreview = async () => {
            if (!selectedSessionId) {
                setSelectedSessionPreview(null);
                setSelectedSourceSetupId(null);
                return;
            }

            setPreviewLoading(true);
            try {
                const session = await LoadSession(selectedSessionId, true);
                const plateSetups = (session?.SessionSetups || [])
                    .filter((setup) => IsPlateCorSetup(setup))
                    .filter((setup) => !(session?.id === currentSession?.id && setup?.Id === currentSetup?.Id))
                    .map((setup) => {
                        const plateShots = Array.isArray(setup?.Shots) ? setup.Shots : [];
                        const researchQuadShots = plateShots.filter((shot) =>
                            Array.isArray(shot?.LMInfos) &&
                            shot.LMInfos.some((lmInfo) => lmInfo?.LMType === 'RESEARCH_QUAD')
                        );

                        return {
                            setup,
                            shotCount: plateShots.length,
                            researchQuadShotCount: researchQuadShots.length,
                            label: `${setup?.LetterId || '-'} | ${setup?.HeadSN || setup?.BaseComponentSN || setup?.ClubId || 'Unknown'}`
                        };
                    });

                const defaultSourceSetupId = plateSetups[0]?.setup?.Id || null;
                setSelectedSourceSetupId(defaultSourceSetupId);

                setSelectedSessionPreview({
                    session,
                    plateSetups
                });
            } catch (error) {
                setSelectedSessionPreview({
                    session: null,
                    plateSetups: [],
                    error: error?.message || 'Unable to load selected session.'
                });
                setSelectedSourceSetupId(null);
            } finally {
                setPreviewLoading(false);
            }
        };

        loadPreview();
    }, [selectedSessionId, currentSession?.id, currentSetup?.Id]);

    const columns = useMemo(() => ([
        { key: 'Date', name: 'Date', width: 170, resizable: false },
        { key: 'UserName', name: 'Golfer', width: 120, resizable: false },
        { key: 'TaskID', name: 'Task ID', width: 160, resizable: false },
        { key: 'Description', name: 'Description', minWidth: 220, resizable: false }
    ]), []);

    const rows = useMemo(() => {
        const normalizedFilter = filterText.trim().toLowerCase();

        return sessions
            .filter((session) => {
                if (!normalizedFilter) {
                    return true;
                }

                const searchText = [
                    session?.Description,
                    session?.UserName,
                    session?.TaskID,
                    session?.Date
                ].filter(Boolean).join(' ').toLowerCase();

                return searchText.includes(normalizedFilter);
            })
            .map((session) => ({
                ...session,
                Date: session?.Date ? new Date(session.Date).toLocaleString().replace(',', '') : '',
                selected: session?.id === selectedSessionId
            }));
    }, [filterText, selectedSessionId, sessions]);

    const selectedPreviewReady = !!selectedSessionPreview && !previewLoading;
    const selectedSourcePlate = selectedPreviewReady
        ? (selectedSessionPreview?.plateSetups || []).find((entry) => entry.setup?.Id === selectedSourceSetupId) || null
        : null;
    const selectedPreviewHasImportablePlate = !!selectedSourcePlate && selectedSourcePlate.shotCount > 0;

    const handleClose = () => {
        onComplete?.({ valid: false });
    };

    const handleImport = () => {
        if (!selectedSessionId || !selectedPreviewHasImportablePlate) {
            return;
        }

        onComplete?.({
            valid: true,
            sessionId: selectedSessionId,
            sourceSetupId: selectedSourceSetupId,
            replaceExisting
        });
    };

    return (
        <div className='modal-container' style={{ height: '700px', maxWidth: '900px' }}>
            <button className='modal-close-button' onClick={handleClose}><Cross2Icon width='30px' height='30px' /></button>
            <div className='modal-header'>Import Plate shots</div>
            <div className='modal-content' style={{ justifyContent: 'start', gap: '12px' }}>
                <div style={{ width: '100%' }}>
                    <input
                        type='text'
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        placeholder='Filter sessions by date, golfer, task, or description'
                        style={{ width: '100%', height: '36px', padding: '6px 10px', boxSizing: 'border-box' }}
                    />
                </div>
                <div style={{ width: '100%', height: '260px' }}>
                    {loading ? (
                        <div>Loading sessions...</div>
                    ) : (
                        <DataGrid
                            className={theme === 'dark' ? 'rdg-dark' : 'rdg-light'}
                            style={{ height: '100%', contain: 'none' }}
                            columns={columns}
                            rows={rows}
                            onCellClick={(args) => setSelectedSessionId(args.row?.id || null)}
                            rowClass={(row) => row.selected ? 'row-selected' : 'row-unselected'}
                        />
                    )}
                </div>
                <div style={{ width: '100%', minHeight: '140px', padding: '12px', border: '1px solid #666', borderRadius: '8px', boxSizing: 'border-box' }}>
                    {!selectedSessionId && (
                        <div>Select a source session to preview its Plate setups.</div>
                    )}
                    {previewLoading && (
                        <div>Loading source session...</div>
                    )}
                    {selectedPreviewReady && (
                        <>
                            <div><b>Source:</b> {selectedSessionPreview.session?.Description || '(no description)'}</div>
                            <div><b>Golfer:</b> {selectedSessionPreview.session?.UserName || 'Unknown'}</div>
                            <div><b>Date:</b> {selectedSessionPreview.session?.Date ? new Date(selectedSessionPreview.session.Date).toLocaleString().replace(',', '') : 'Unknown'}</div>
                            <div><b>Plate setups:</b> {selectedSessionPreview.plateSetups?.length || 0}</div>
                            {selectedSessionPreview.plateSetups?.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                    <label>
                                        <b>Source Plate:</b>
                                        <select
                                            style={{ marginLeft: '8px' }}
                                            value={selectedSourceSetupId || ''}
                                            onChange={(e) => setSelectedSourceSetupId(e.target.value || null)}
                                        >
                                            {selectedSessionPreview.plateSetups.map((entry) => (
                                                <option key={entry.setup?.Id} value={entry.setup?.Id}>
                                                    {entry.label} ({entry.shotCount} shots)
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            )}
                            {selectedSourcePlate && (
                                <>
                                    <div><b>Selected Plate shots:</b> {selectedSourcePlate.shotCount}</div>
                                    <div><b>Selected Research Quad shots:</b> {selectedSourcePlate.researchQuadShotCount}</div>
                                </>
                            )}
                            {selectedSessionPreview.error && (
                                <div style={{ color: '#ff7b7b', marginTop: '8px' }}>{selectedSessionPreview.error}</div>
                            )}
                            {selectedSessionPreview.plateSetups?.length === 0 && (
                                <div style={{ color: '#ffcc66', marginTop: '8px' }}>The selected session has no importable Plate setups.</div>
                            )}
                            {selectedSourcePlate && selectedSourcePlate.shotCount === 0 && (
                                <div style={{ color: '#ffcc66', marginTop: '8px' }}>The selected Plate setup has no shots to import.</div>
                            )}
                        </>
                    )}
                </div>
                <div style={{ width: '100%', display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                            type='radio'
                            name='plate-import-mode'
                            checked={replaceExisting}
                            onChange={() => setReplaceExisting(true)}
                        />
                        <span>Replace current Plate shots</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                            type='radio'
                            name='plate-import-mode'
                            checked={!replaceExisting}
                            onChange={() => setReplaceExisting(false)}
                        />
                        <span>Append to current Plate shots</span>
                    </label>
                </div>
            </div>
            <div className='modal-footer'>
                <button className='button' style={{ width: '120px' }} disabled={!selectedPreviewHasImportablePlate} onClick={handleImport}>Import</button>
            </div>
        </div>
    );
}

export default ImportPlateShotsModal;
